import { PUZZLES } from "./puzzles.js";
import { PuzzleCatalog } from "./puzzle-catalog.js";
import { BrowserGameStore } from "./game-store.js";
import { GameController } from "./game-controller.js";
import { PixiBoardRenderer, DomBoardRenderer } from "./board-renderer.js";
import { GameView } from "./ui/game-view.js";
import { AnimationClock } from "./animation/clock.js";
import { CatPortraits } from "./animation/cat-portraits.js";
import { GameAudio } from "./audio/game-audio.js";
import { Confetti } from "./animation/confetti.js";
import { GameFeedback } from "./ui/game-feedback.js";
import { createRewardedCatHint } from "./rewarded-hints.js";

/** Compose the existing game adapters for a document with already rendered UI. */
export async function initializeGame({ assets, host, monetizedHints = false }) {
  let controller, clock, motionPreference, onMotionChange;
  let portraits, audio, confetti, feedback, renderer, fallbackRenderer, view;
  let unsubscribeHost;
  let disposed = false;
  const pauseReasons = new Set();
  const setPaused = (reason, paused) => {
    if (disposed) return;
    if (paused) pauseReasons.add(reason);
    else pauseReasons.delete(reason);
    view.setHostPaused(pauseReasons.size > 0);
  };
  const clean = (action) => { try { action(); } catch {} };
  const dispose = () => {
    if (disposed) return;
    disposed = true;
    clean(() => unsubscribeHost?.());
    if (onMotionChange) clean(() => motionPreference.removeEventListener("change", onMotionChange));
    clean(() => controller?.pause());
    if (view) clean(() => view.destroy());
    else {
      clean(() => portraits?.destroy());
      if (feedback) clean(() => feedback.destroy());
      else {
        clean(() => audio?.destroy());
        clean(() => confetti?.destroy());
      }
      clean(() => renderer?.destroy());
      clean(() => fallbackRenderer?.destroy());
    }
    clean(() => clock?.destroy());
  };
  try {
    const query = new URLSearchParams(location.search);
    const playtest = query.get("playtest");
    const key =
      playtest !== null
        ? `meowdoku.playtest${playtest ? `.${playtest}` : ""}.v1`
        : "meowdoku.garden.v1";
    let storage;
    try {
      storage = localStorage;
    } catch {
      storage = {
        getItem() {
          throw new Error("Storage unavailable");
        },
        setItem() {
          throw new Error("Storage unavailable");
        },
      };
    }
    controller = new GameController({
      catalog: new PuzzleCatalog(PUZZLES),
      store: new BrowserGameStore(storage, key),
      monetizedHints,
    });
    clock = new AnimationClock();
    motionPreference = window.matchMedia("(prefers-reduced-motion: reduce)");
    const reducedMotion = () => motionPreference.matches;
    portraits = new CatPortraits({ clock, reducedMotion, asset: assets.catRig });
    audio = new GameAudio({ assets });
    void audio.preload();
    confetti = new Confetti({ clock, reducedMotion });
    feedback = new GameFeedback({ audio, confetti });
    renderer = new PixiBoardRenderer({ clock, reducedMotion, catAsset: assets.catRig });
    fallbackRenderer = new DomBoardRenderer();
    view = new GameView({
      assets,
      controller,
      clock,
      portraits,
      feedback,
      renderer,
      fallbackRenderer,
      beforeWonRoundExit: host?.beforeWonRoundExit
        ? (context) => host.beforeWonRoundExit({ ...context, saveKey: key })
        : undefined,
      requestRewardedCatHint: monetizedHints ? createRewardedCatHint({
        controller,
        ads: host?.ads,
        setPaused: (paused) => setPaused("reward", paused),
        isDisposed: () => disposed,
      }) : undefined,
    });
    unsubscribeHost = host?.subscribePaused?.((paused) => setPaused("host", paused));
    await view.mount();
    onMotionChange = () => {
      view.renderer.refreshMotion();
      view.lives.refreshMotion();
      feedback.refreshMotion();
      portraits.draw(clock.time);
      clock.wake();
    };
    motionPreference.addEventListener("change", onMotionChange);
    return {
      setOfflineReady() { if (!disposed) view.setOfflineReady(); },
      dispose,
    };
  } catch (error) {
    dispose();
    throw error;
  }
}
